/* eslint-disable max-lines */
import {
  ReportsSubscriptionData,
  ReportsSubscriptionLog
} from '@models/reports';
import createReducer from '../../reducers/utils/createReducer';
import * as c from './types';
import moment from 'moment';

export interface DashboardData {
  id: string;
  title: string;
  externalId: string;
  order: number;
  lang: string;
  filters: [
    {
      id: string;
      dashboardId: string;
      name: string;
      type: string;
      order: number;
      lang: string;
    }
  ];
}

export interface GroupedDashboardData {
  id: string;
  title: string;
  order: number;
  lang: string;
  dashboards: {
    id: string;
    title: string;
    externalId: string;
    order: number;
    lang: string;
  }[];
}
export interface CreateReportData {
  id?: string;
  lang: string;
  name: string;
  description: string;
  recipients: string[];
  frequency: {
    unit: string;
    amount: number;
    days: number[];
    time: moment.Moment;
    timezone: string;
    since: Date;
  };
  selectedPlots: {
    id: string;
    title: string;
    allDashboards: {
      id: string;
      title: string;
      externalId: string;
      order: number;
      lang: string;
    }[];
    dashboards: {
      id: string;
      dashboardId: string;
      order: number;
      new?: boolean;
      allFilters: {
        id: string;
        dashboardId: string;
        name: string;
        type: string;
        order: number;
      }[];
      filters: {
        id?: string;
        type: string;
        name: string;
        dashboardFilterId: string;
        value: any;
      }[];
    }[];
  }[];
}
export interface ReportState {
  data: any;
  sensorData: any;
  isLoading: boolean;
  error: any;
  reportSubscriptionsData: ReportsSubscriptionData[];
  isReportSubscriptionsDataLoading: boolean;
  reportSubscriptionsDataError: any;
  reportSubscriptionsDetailData: ReportsSubscriptionData | null;
  isReportSubscriptionsDetailLoading: boolean;
  reportSubscriptionsDetailError: any;
  reportSubscriptionsLogsData: ReportsSubscriptionLog[];
  isReportSubscriptionsLogsLoading: boolean;
  reportSubscriptionsLogsError: any;
  isDeleteReportSubscriptionLoading: boolean;
  deleteReportSubscriptionError: any;
  deleteDialog: boolean;
  cancelDialog: boolean;
  activeRowId: string | null;
  isCancelReportSubscriptionInstanceLoading: boolean;
  cancelReportSubscriptionInstanceError: any;
  dashboardData: DashboardData[];
  groupedDashboardData: GroupedDashboardData[];
  isDashboardDataLoading: boolean;
  isGroupedDashboardDataLoading: boolean;
  createReportData: CreateReportData;
  isCreateReportSubscriptionLoading: boolean;
  isUpdateReportSubscriptionLoading: boolean;
  sendReportToRecipientsLoading: boolean;
  SendReportToRecipientsError: any;
}
const initialCreateReportData: CreateReportData = {
  name: '',
  lang: 'es',
  frequency: {
    unit: 'day',
    amount: 1,
    days: [],
    time: moment(),
    timezone: 'America/Monterrey',
    since: new Date()
  },
  description: '',
  recipients: [],
  selectedPlots: []
};

const initialState: ReportState = {
  data: {},
  sensorData: {},
  isLoading: false,
  error: null,
  reportSubscriptionsData: [],
  isReportSubscriptionsDataLoading: false,
  reportSubscriptionsDataError: null,
  reportSubscriptionsDetailData: null,
  isReportSubscriptionsDetailLoading: false,
  reportSubscriptionsDetailError: null,
  reportSubscriptionsLogsData: [],
  isReportSubscriptionsLogsLoading: false,
  reportSubscriptionsLogsError: null,
  isDeleteReportSubscriptionLoading: false,
  deleteReportSubscriptionError: null,
  deleteDialog: false,
  cancelDialog: false,
  activeRowId: null,
  dashboardData: [],
  groupedDashboardData: [],
  isDashboardDataLoading: false,
  isGroupedDashboardDataLoading: false,
  createReportData: initialCreateReportData,
  isCreateReportSubscriptionLoading: false,
  isUpdateReportSubscriptionLoading: false,
  isCancelReportSubscriptionInstanceLoading: false,
  cancelReportSubscriptionInstanceError: null,
  sendReportToRecipientsLoading: false,
  SendReportToRecipientsError: null
};

export default createReducer(initialState, {
  [c.GET_YESTERDAY_MACHINES_SHIFTS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isLoading: true
  }),
  [c.GET_YESTERDAY_MACHINES_SHIFTS_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      data: payload,
      isLoading: false
    };
  },
  [c.GET_YESTERDAY_MACHINES_SHIFTS_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isLoading: false
    };
  },
  [c.GET_REPORT_DASHBOARDS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isDashboardDataLoading: true
  }),
  [c.GET_REPORT_DASHBOARDS_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      dashboardData: payload,
      isDashboardDataLoading: false
    };
  },
  [c.GET_REPORT_DASHBOARDS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isDashboardDataLoading: false
    };
  },
  [c.GET_REPORT_GROUPED_DASHBOARDS_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isGroupedDashboardDataLoading: true
  }),
  [c.GET_REPORT_GROUPED_DASHBOARDS_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      groupedDashboardData: payload,
      isGroupedDashboardDataLoading: false
    };
  },
  [c.GET_REPORT_GROUPED_DASHBOARDS_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isGroupedDashboardDataLoading: false
    };
  },
  [c.MODIFY_CREATE_REPORT_DATA]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      createReportData: payload
    };
  },
  [c.CREATE_REPORT_SUBSCRIPTION_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isCreateReportSubscriptionLoading: true
  }),
  [c.CREATE_REPORT_SUBSCRIPTION_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      data: payload,
      isCreateReportSubscriptionLoading: false
    };
  },
  [c.CREATE_REPORT_SUBSCRIPTION_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isCreateReportSubscriptionLoading: false
    };
  },
  [c.UPDATE_REPORT_SUBSCRIPTION_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateReportSubscriptionLoading: true
  }),
  [c.UPDATE_REPORT_SUBSCRIPTION_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      data: payload,
      isUpdateReportSubscriptionLoading: false
    };
  },
  [c.UPDATE_REPORT_SUBSCRIPTION_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isUpdateReportSubscriptionLoading: false
    };
  },
  [c.RESET_CREATE_REPORT_DATA]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      createReportData: initialCreateReportData
    };
  },
  [c.GET_ALL_REPORT_SUBSCRIPTION_REQUEST]: (state = initialState) => ({
    ...state,
    reportSubscriptionsDataError: null,
    isReportSubscriptionsDataLoading: true
  }),
  [c.GET_ALL_REPORT_SUBSCRIPTION_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsData: payload,
      isReportSubscriptionsDataLoading: false
    };
  },
  [c.GET_ALL_REPORT_SUBSCRIPTION_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsDataError: payload,
      isReportSubscriptionsDataLoading: false
    };
  },
  [c.GET_REPORT_SUBSCRIPTION_DETAIL_REQUEST]: (state = initialState) => ({
    ...state,
    reportSubscriptionsDetailError: null,
    isReportSubscriptionsDetailLoading: true
  }),
  [c.GET_REPORT_SUBSCRIPTION_DETAIL_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsDetailData: payload,
      isReportSubscriptionsDetailLoading: false
    };
  },
  [c.GET_REPORT_SUBSCRIPTION_DETAIL_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsDetailError: payload,
      isReportSubscriptionsDetailLoading: false
    };
  },
  [c.GET_REPORT_SUBSCRIPTION_LOGS_REQUEST]: (state = initialState) => ({
    ...state,
    reportSubscriptionsLogsError: null,
    isReportSubscriptionsLogsLoading: true
  }),
  [c.GET_REPORT_SUBSCRIPTION_LOGS_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsLogsData: payload,
      isReportSubscriptionsLogsLoading: false
    };
  },
  [c.GET_REPORT_SUBSCRIPTION_LOGS_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsLogsError: payload,
      isReportSubscriptionsLogsLoading: false
    };
  },

  [c.DELETE_REPORT_SUBSCRIPTION_REQUEST]: (state = initialState) => ({
    ...state,
    deleteReportSubscriptionError: null,
    isDeleteReportSubscriptionLoading: true
  }),
  [c.DELETE_REPORT_SUBSCRIPTION_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      // reportSubscriptionsLogsData: payload,
      isDeleteReportSubscriptionLoading: false
    };
  },
  [c.DELETE_REPORT_SUBSCRIPTION_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      deleteReportSubscriptionError: payload,
      isDeleteReportSubscriptionLoading: false
    };
  },
  [c.TOGGLE_DIALOG]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      ...(payload.type === 'cancel' && {
        cancelDialog: payload.value,
        activeRowId: payload.id
      }),
      ...(payload.type === 'delete' && {
        deleteDialog: payload.value,
        activeRowId: payload.id
      })
    };
  },
  [c.CANCEL_REPORT_SUBSCRIPTION_INSTANCE_REQUEST]: (state = initialState) => ({
    ...state,
    cancelReportSubscriptionInstanceError: null,
    isCancelReportSubscriptionInstanceLoading: true
  }),
  [c.CANCEL_REPORT_SUBSCRIPTION_INSTANCE_SUCCESS]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      isCancelReportSubscriptionInstanceLoading: false
    };
  },
  [c.CANCEL_REPORT_SUBSCRIPTION_INSTANCE_ERROR]: (
    state = initialState,
    { payload }
  ) => {
    if (!payload) return state;
    return {
      ...state,
      cancelReportSubscriptionInstanceError: payload,
      isCancelReportSubscriptionInstanceLoading: false
    };
  },
  [c.CLEAR_REPORT_SUBSCRIPTION_DETAIL]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      reportSubscriptionsDetailData: null,
      isReportSubscriptionsDetailLoading: false
    };
  },
  [c.SEND_REPORT_TO_RECIPIENTS_REQUEST]: (state = initialState) => ({
    ...state,
    SendReportToRecipientsError: null,
    sendReportToRecipientsLoading: true
  }),
  [c.SEND_REPORT_TO_RECIPIENTS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      sendReportToRecipientsLoading: false
    };
  },
  [c.SEND_REPORT_TO_RECIPIENTS_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      SendReportToRecipientsError: payload,
      sendReportToRecipientsLoading: false
    };
  }
});
